import { useState, type ReactNode } from "react";

export type EntityTab = { key: string; label: string; badge?: number; content: ReactNode };

/** Full-screen entity page shell: sticky header + tab strip + scrollable body. */
export function EntityTabs({ header, tabs }: { header: ReactNode; tabs: EntityTab[] }) {
  const [active, setActive] = useState(tabs[0]?.key);
  const current = tabs.find((t) => t.key === active) ?? tabs[0];

  return (
    <div className="mx-auto max-w-3xl">
      <div className="border-b bg-card px-5 pt-5">
        {header}
        <nav className="mt-4 flex gap-1 overflow-x-auto" role="tablist">
          {tabs.map((tab) => {
            const isActive = tab.key === current?.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActive(tab.key)}
                className={`shrink-0 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:border-border hover:text-foreground"
                }`}
              >
                {tab.label}
                {tab.badge != null && (
                  <span
                    className={`ml-1.5 rounded-full px-1.5 py-0.5 text-xs font-semibold tabular-nums ${
                      isActive ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>
      <div className="p-5">{current?.content}</div>
    </div>
  );
}
